import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { UserProfileComponent } from '../../auth/user-profile/user-profile.component';
import { SessionMonitorComponent } from '../../auth/session-monitor/session-monitor.component';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, UserProfileComponent, SessionMonitorComponent],
  template: `
    <header class="app-header">
      <div class="header-container">
        <a routerLink="/" class="logo">
          <div class="logo-icon">A</div>
          <span class="logo-text">Appointment Bot</span>
        </a>

        <button class="menu-toggle" (click)="toggleMenu()" [class.active]="isMenuOpen" aria-label="Toggle menu">
          <span class="bar"></span>
          <span class="bar"></span>
          <span class="bar"></span>
        </button>

        <div class="expandable-content" [class.open]="isMenuOpen">
          <div class="mobile-menu-inner">
            <div class="user-section">
              <app-user-profile></app-user-profile>
            </div>
          </div>
        </div>
      </div>

      <app-session-monitor></app-session-monitor>
    </header>
  `,
  styles: [`
    .app-header {
      background: var(--ap-surface, #fff);
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      padding: 0 1.5rem;
      position: sticky;
      top: 0;
      z-index: 1000;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: 1400px;
      margin: 0 auto;
      min-height: 56px;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 10px;
      text-decoration: none;
    }

    .logo-icon {
      width: 34px;
      height: 34px;
      background: var(--ap-primary, #2F5BDA);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 700;
      font-size: 17px;
      flex-shrink: 0;
    }

    .logo-text {
      color: var(--ap-text, #0B1220);
      font-size: 1rem;
      font-weight: 650;
      letter-spacing: -0.01em;
    }

    .logo:hover .logo-text {
      color: var(--ap-primary, #2F5BDA);
    }

    /* Desktop */
    .expandable-content {
      display: flex;
      align-items: center;
    }

    .menu-toggle {
      display: none;
    }

    .user-section {
      display: flex;
      align-items: center;
    }

    /* Mobile */
    @media (max-width: 768px) {
      .app-header {
        padding: 0 0.75rem;
      }

      .header-container {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        min-height: 56px;
      }

      .logo-text {
        font-size: 0.9rem;
      }

      .menu-toggle {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        width: 28px;
        height: 28px;
        background: transparent !important;
        border: none;
        cursor: pointer;
        padding: 0;
        outline: none;
        z-index: 1001;
        flex-shrink: 0;
        -webkit-appearance: none;
        appearance: none;
        -webkit-tap-highlight-color: transparent;
        box-shadow: none !important;
      }

      .menu-toggle:focus,
      .menu-toggle:active,
      .menu-toggle:focus-visible {
        background: transparent !important;
        box-shadow: none !important;
        outline: none;
      }

      .menu-toggle .bar {
        display: block;
        width: 20px;
        height: 2px;
        background: var(--ap-text-soft, #4B5563);
        border-radius: 2px;
        transition: all 0.25s ease;
      }

      .menu-toggle.active .bar:nth-child(2) { opacity: 0; transform: scaleX(0); }
      .menu-toggle.active .bar:nth-child(1) { transform: translateY(6px) rotate(45deg); }
      .menu-toggle.active .bar:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }

      .expandable-content {
        position: absolute;
        top: 56px;
        left: 0;
        width: 100%;
        height: 0;
        overflow: hidden;
        background: var(--ap-surface, #fff);
        border-top: 1px solid var(--ap-border, #E3E6EB);
        transition: height 0.25s ease-in-out;
        flex-direction: column;
        align-items: flex-start;
        box-shadow: 0 8px 24px rgba(11,18,32,0.08);
      }

      .expandable-content.open {
        height: calc(100vh - 56px);
        overflow-y: auto;
      }

      /* Inner wrapper carries the padding — never animates,
         so content never jumps when menu opens/closes */
      .mobile-menu-inner {
        padding: 1rem;
        width: 100%;
      }

      .user-section {
        width: 100%;
        justify-content: flex-start;
      }
    }

    @media (max-width: 480px) {
      .app-header {
        padding: 0 0.5rem;
      }

      .logo-text {
        font-size: 0.85rem;
      }
    }
  `]
})
export class HeaderComponent implements OnInit, OnDestroy {
    isMenuOpen = false;
    private routerSubscription?: Subscription;
    
    constructor(private router: Router) {}
    
    ngOnInit() {
        // Subscribe to router events to close menu on navigation
        this.routerSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            // Close the mobile menu when navigation occurs
            if (this.isMenuOpen) {
                this.isMenuOpen = false;
            }
        });
    }
    
    ngOnDestroy() {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen;
    }
    
    // Optional: Close the menu if the window is resized to a larger screen
    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        if (window.innerWidth > 768) {
            this.isMenuOpen = false;
        }
    }
}